<div class="p-3 pt-0 min-login-size auth-screen">
    <div class="login-logo"></div>
    <mat-card>
        <mat-card-header>
            <mat-card-title>Create your account</mat-card-title>
        </mat-card-header>
        <form [formGroup]="signupForm" (ngSubmit)="stepper.selectedIndex === 0 ? continueToPlan(stepper) : onSubmit()">
            <mat-card-content>
                @if (recoveringAttempt) { <p role="status">To resume signup, enter your original details and password.</p> }
                @if (optionsLoading) {
                    <p role="status">Loading available plans…</p>
                }
                @if (optionsError) {
                    <p role="alert">{{ optionsError }}</p>
                    <button type="button" mat-button (click)="loadSignupOptions()">Try again</button>
                }
                @if (error || unmappedErrors.length) {
                    <div id="signup-error-summary" class="signup-error-summary" role="alert" tabindex="-1">
                        @if (error) { <p>{{ error }}</p> }
                        @for (message of unmappedErrors; track $index) { <p>{{ message }}</p> }
                    </div>
                }
                <mat-stepper #stepper [linear]="true" animationDuration="0ms">
                    <mat-step label="Details" [stepControl]="signupForm">
                        <p class="mat-text-muted">To join a team, use the invitation link in your email.</p>
                        <div class="row">
                            <div class="col-12">
                                <mat-form-field>
                                    <mat-label>Company name</mat-label>
                                    <input matInput type="text" formControlName="company_name" name="company_name" autocomplete="organization" required />
                                    @if (fieldError('company_name')) {
                                        <mat-error>{{ fieldError('company_name') }}</mat-error>
                                    }
                                </mat-form-field>
                            </div>
                            <div class="col-12 col-md-6">
                                <mat-form-field>
                                    <mat-label>Full name</mat-label>
                                    <input matInput type="text" formControlName="name" name="name" autocomplete="name" required />
                                    @if (fieldError('name')) {
                                        <mat-error>{{ fieldError('name') }}</mat-error>
                                    }
                                </mat-form-field>
                            </div>
                            <div class="col-12 col-md-6">
                                <mat-form-field subscriptSizing="dynamic">
                                    <mat-label>Email</mat-label>
                                    <input matInput type="email" formControlName="email" name="email" autocomplete="email" autocapitalize="none" spellcheck="false" required />
                                    @if (fieldError('email')) {
                                        <mat-error>{{ fieldError('email') }}</mat-error>
                                    }
                                </mat-form-field>
                            </div>
                            <div class="col-12 col-md-6 password-column">
                                <mat-form-field subscriptSizing="dynamic">
                                    <mat-label>Password</mat-label>
                                    <input matInput [type]="showPassword ? 'text' : 'password'" formControlName="password" name="password" autocomplete="new-password" required />
                                    <button mat-icon-button matSuffix type="button" aria-label="Password requirements" #passwordHelp="matTooltip" (click)="passwordHelp.toggle()" [matTooltip]="passwordHelpText"><mat-icon>info_outline</mat-icon></button>
                                    <button mat-icon-button matSuffix type="button" [attr.aria-label]="showPassword ? 'Hide password' : 'Show password'" [attr.aria-pressed]="showPassword" (click)="showPassword = !showPassword"><mat-icon>{{ showPassword ? 'visibility_off' : 'visibility' }}</mat-icon></button>
                                    @if (fieldError('password')) {
                                        <mat-error>{{ fieldError('password') }}</mat-error>
                                    }
                                </mat-form-field>
                                @if (f.password.value || submitted) {
                                    <ul class="password-checks" aria-label="Password requirements" aria-live="polite">
                                        @for (rule of passwordRules; track rule.label) {
                                            @let passed = rule.matches(f.password.value ?? '');
                                            <li [class.passed]="passed"><mat-icon aria-hidden="true">{{ passed ? 'check_circle' : 'cancel' }}</mat-icon><span>{{ rule.label }}</span><span class="visually-hidden">{{ passed ? ': passed' : ': not met' }}</span></li>
                                        }
                                    </ul>
                                }
                            </div>
                            <div class="col-12 col-md-6">
                                <mat-form-field>
                                    <mat-label>Confirm password</mat-label>
                                    <input matInput [type]="showPasswordConfirmation ? 'text' : 'password'" formControlName="password_confirmation" name="password_confirmation" autocomplete="new-password" required />
                                    <button mat-icon-button matSuffix type="button" [attr.aria-label]="showPasswordConfirmation ? 'Hide confirmation password' : 'Show confirmation password'" [attr.aria-pressed]="showPasswordConfirmation" (click)="showPasswordConfirmation = !showPasswordConfirmation"><mat-icon>{{ showPasswordConfirmation ? 'visibility_off' : 'visibility' }}</mat-icon></button>
                                    @if (fieldError('password_confirmation')) {
                                        <mat-error>{{ fieldError('password_confirmation') }}</mat-error>
                                    }
                                </mat-form-field>
                            </div>
                        </div>
                        <div class="mt-3 d-flex align-items-center gap-2 flex-wrap">
                            <button [type]="stepper.selectedIndex === 0 ? 'submit' : 'button'" mat-raised-button color="primary">Choose plan</button>
                            <a mat-button color="primary" routerLink="/login">Return to login</a>
                        </div>
                    </mat-step>
                    <mat-step label="Plan">
                        <ng-template matStepContent>
                            @if (options) {
                                <fieldset class="signup-plans" aria-labelledby="signup-plan-heading">
                                    <legend id="signup-plan-heading">Choose your plan</legend>
                                    @if (planPreferenceMessage && !f.plan.value) { <p role="status">{{ planPreferenceMessage }}</p> }
                                    <div class="pricing-grid">
                                        <label class="pricing-card payg-plan" [class.plan-selected]="f.plan.value === 'payg'">
                                            <span class="plan-card-heading"><span>Pay as you go</span><input type="radio" formControlName="plan" name="plan" value="payg" aria-label="PAYG" /></span>
                                            <span class="plan-description">For occasional surveying</span>
                                            <span class="plan-price">£0<span>to get started</span></span>
                                            <span class="plan-subline">No payment needed</span>
                                            <ul class="plan-features">
                                                <li>100 free credits</li>
                                                <li>Buy credit packs when you need them</li>
                                                <li>No monthly subscription</li>
                                            </ul>
                                        </label>
                                        @if (options.plan_signup_enabled || proSelected) {
                                            <section class="pricing-card pro-plan-card" [class.plan-selected]="proSelected" [class.plan-unavailable]="!planAvailable(proPlan)">
                                                <label class="plan-hit-area">
                                                    <input type="radio" name="plan" aria-label="Pro" [checked]="proSelected" (change)="selectPro()" [disabled]="loading || !planAvailable(proPlan)" />
                                                </label>
                                                <span class="plan-card-heading">Pro</span>
                                                <span class="plan-description">For regular surveying</span>
                                                @if (proOffer; as offer) {
                                                    <span class="plan-price">{{ offer.amount / 100 | currency:offer.currency.toUpperCase():'symbol':'1.0-2' }}<span>{{ proPlan === 'pro_annual' ? '/year' : '/month' }}</span></span>
                                                } @else {
                                                    <span class="plan-description">Pro is temporarily unavailable.</span>
                                                }
                                                <div class="billing-switch" role="group" aria-label="Pro billing period">
                                                    <button type="button" [attr.aria-pressed]="proPlan === 'pro_monthly'" [disabled]="loading || !planAvailable('pro_monthly')" (click)="switchProPrice('pro_monthly')">Monthly</button>
                                                    <button type="button" [attr.aria-pressed]="proPlan === 'pro_annual'" [disabled]="loading || !planAvailable('pro_annual')" (click)="switchProPrice('pro_annual')">Annual</button>
                                                </div>
                                                @if (proOffer; as offer) {
                                                    <ul class="plan-features">
                                                        <li>{{ offer.included_surveyors }} surveyor seats included</li>
                                                        @for (feature of offer.features; track $index) {
                                                            <li>{{ feature }}</li>
                                                        }
                                                    </ul>
                                                }
                                            </section>
                                        }
                                    </div>
                                </fieldset>
                            }
                            <div class="signup-plan-actions mt-3 d-flex align-items-center gap-2 flex-wrap">
                                <button type="button" mat-button matStepperPrevious [disabled]="loading">Back</button>
                                <button [type]="stepper.selectedIndex === 1 ? 'submit' : 'button'" mat-raised-button color="primary" [disabled]="loading || !selectedPlanAvailable">
                                    @if (loading) {
                                        <mat-icon><mat-spinner diameter="20"></mat-spinner></mat-icon>
                                    } @else {
                                        <span>{{ proSelected ? 'Continue to payment' : 'Create account' }}</span>
                                    }
                                </button>
                            </div>
                        </ng-template>
                    </mat-step>
                </mat-stepper>
            </mat-card-content>
        </form>
    </mat-card>
</div>
